// frontend/src/components/PageHero.js
//
// The header band every interior page opens with: the same black ground and
// the same 3D fall of hair as the landing hero, at banner height. Having one
// component own it means a page cannot drift into its own header treatment,
// and the strand field is configured once for the wide-and-short proportion
// rather than per page.
import React from 'react';
import { Sparkles } from 'lucide-react';
import HairFlow3D from './HairFlow3D';
import { WordsIn } from './Motion';
import { motion, useReducedMotion } from 'framer-motion';
import theme from '../theme';

const EASE = [0.22, 1, 0.36, 1];

export default function PageHero({ eyebrow, title, subtitle, children, height = 300 }) {
  const reduced = useReducedMotion();

  return (
    <section style={{
      position: 'relative', overflow: 'hidden', background: theme.inkDeep,
      minHeight: height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '56px 24px',
    }}>
      {/* widen: at banner proportions the fall would otherwise be squeezed into
          a ribbon down the middle of the band. */}
      <HairFlow3D
        widen={2.4}
        wash={`radial-gradient(ellipse 80% 120% at 50% 10%, ${theme.alpha.gold(0.16)} 0%, transparent 72%)`}
        vignette={`radial-gradient(ellipse 45% 62% at 50% 55%, ${theme.alpha.inkDeep(0.72)} 0%, ${theme.alpha.inkDeep(0.30)} 50%, transparent 80%)`}
      >
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(ellipse 60% 90% at 50% 30%, ${theme.alpha.gold(0.10)} 0%, transparent 70%)`,
        }} />
      </HairFlow3D>

      <div style={{ position: 'relative', maxWidth: 760 }}>
        {eyebrow && (
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 10 }}
            animate={reduced ? false : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, color: theme.gold,
              fontSize: 10, letterSpacing: '0.26em', textTransform: 'uppercase',
              fontFamily: theme.font.body, marginBottom: 18,
            }}>
            <Sparkles size={12} /> {eyebrow}
          </motion.div>
        )}

        <h1 style={{
          fontFamily: theme.font.display,
          fontSize: 'clamp(34px, 6vw, 62px)', fontWeight: 300, color: theme.white,
          lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0, textWrap: 'balance',
        }}>
          <WordsIn text={title} delay={0.22} />
        </h1>

        {subtitle && (
          <motion.p
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={reduced ? false : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            style={{
              color: theme.alpha.white(0.55), fontSize: 15, lineHeight: 1.8,
              maxWidth: 520, margin: '18px auto 0', fontWeight: 300,
              fontFamily: theme.font.body,
            }}>
            {subtitle}
          </motion.p>
        )}

        {children && (
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={reduced ? false : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            style={{ marginTop: 26 }}>
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
}
